import { createContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import analyticsService from "./AnalyticsService";
import {
  AnalyticsErrorBoundary,
  setupGlobalErrorTracking,
} from "./utils/errorTracker";
import { usePerformanceTracker } from "./utils/performanceTracker";
import { useSessionTracker } from "./utils/sessionTracker";
import { usePageTracker } from "./utils/pageTracker";
import { useInteractionTracker } from "./utils/interactionTracker";
import { CUSTOM_DIMENSIONS } from "./constants";

const GAContext = createContext(null);

const GAProvider = ({ children }) => {
  const authReducer = useSelector((state) => state?.authReducer);
  const orgReducer = useSelector((state) => state?.orgReducer);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const selectedCard = useSelector(
    (state) => state?.dashboardReducer?.selectedCard,
  );

  const analyticsContext = useMemo(() => {
    const selectedFI = orgReducer?.selectedFI || {};
    return {
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION]: selectedFI?.actualName || null,
      [CUSTOM_DIMENSIONS.IDRSSD]: selectedFI?.idrssd || null,
      [CUSTOM_DIMENSIONS.INSTITUTION_TYPE]: selectedFI?.fi_Type || null,
      [CUSTOM_DIMENSIONS.DASHBOARD]:
        currentDashboard?.CONFIG?.pageHeader?.pageTitle || null,
      [CUSTOM_DIMENSIONS.USER_ROLE]: authReducer?.userData?.role || null,
      [CUSTOM_DIMENSIONS.SUBSCRIPTION_TYPE]:
        authReducer.userData.subscriptionName || null,
      [CUSTOM_DIMENSIONS.NAVIGATED_FROM_CARD]:
        currentDashboard?.KEY === 2
          ? selectedCard?.commercial_title || null
          : selectedCard?.retail_title || null,
    };
  }, [authReducer, orgReducer, currentDashboard, selectedCard]);

  useEffect(() => {
    analyticsService.init({ disableOnLocal: false });
    // analyticsService.init();
  }, []);

  useEffect(() => {
    analyticsService.setContext(analyticsContext);
  }, [analyticsContext]);

  useEffect(() => {
    if (!authReducer?.isLogin) {
      analyticsService.clearUser();
      return;
    }

    const profile = {
      [CUSTOM_DIMENSIONS.USER_ID]:
        authReducer?.userData?.client_User_Id || null,
      [CUSTOM_DIMENSIONS.USER_NAME]: `${authReducer?.userData?.firstName} ${authReducer?.userData?.lastName}`,
      [CUSTOM_DIMENSIONS.USER_ROLE]: analyticsContext?.user_role || null,
      [CUSTOM_DIMENSIONS.ORGANIZATION]:
        authReducer?.userData?.orgnizationName || null,
      [CUSTOM_DIMENSIONS.FINANCIAL_INSTITUTION]:
        analyticsContext?.financial_institution_name || null,
      [CUSTOM_DIMENSIONS.IDRSSD]: analyticsContext?.idrssd || null,
      [CUSTOM_DIMENSIONS.SUBSCRIPTION_TYPE]: analyticsContext.subscription_type,
    };

    const userId = authReducer?.userData?.client_User_Id || null;

    analyticsService.setUser(userId, profile);
    analyticsService.setLoginTime(
      authReducer?.loggedInTime || new Date().toISOString(),
    );
  }, [authReducer, orgReducer, analyticsContext.subscription_type]);

  useEffect(() => {
    return setupGlobalErrorTracking();
  }, []);

  usePageTracker();
  // usePerformanceTracker();
  // useSessionTracker();
  // useInteractionTracker();

  return (
    <GAContext.Provider value={analyticsContext}>
      <AnalyticsErrorBoundary>{children}</AnalyticsErrorBoundary>
    </GAContext.Provider>
  );
};

GAProvider.propTypes = {
  children: PropTypes.node,
};

export default GAProvider;
export { GAContext };
